import React, { useState } from 'react';
import styled from 'styled-components';
import {
  FiDownload,
  FiSearch,
  FiFilter,
  FiCheck,
  FiClock,
  FiX,
} from 'react-icons/fi';
import Button from '../common/Button';
import Card from '../common/Card';
import { reportService, adminService } from '../../services/api';

const ReportContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const ReportHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ReportTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  margin: 0;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[3]};
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: white;
  min-width: 150px;
`;

const FilterInput = styled.input`
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: white;
`;

const FilterActions = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-left: auto;
`;

// Table styles
const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
`;

const TableHeadCell = styled.th`
  padding: ${({ theme }) => theme.spacing[3]};
  text-align: left;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  white-space: nowrap;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  
  ${({ status, theme }) => {
    if (status === 'approved') {
      return `
        background-color: ${theme.colors.success}15;
        color: ${theme.colors.success};
      `;
    } else if (status === 'pending') {
      return `
        background-color: ${theme.colors.warning}15;
        color: ${theme.colors.warning};
      `;
    } else if (status === 'challenged') {
      return `
        background-color: ${theme.colors.error}15;
        color: ${theme.colors.error};
      `;
    }
    return '';
  }}
  
  svg {
    margin-right: 4px;
    width: 12px;
    height: 12px;
  }
`;

const SummaryFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing[4]};
  padding-top: ${({ theme }) => theme.spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const FooterText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  
  span {
    font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[6]};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// Employee grouping (REQ #18): one collapsible summary row per employee.
const GroupRow = styled.tr`
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
`;

const GroupCell = styled.td`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.text};
`;

const GroupMetaCell = styled.td`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ExpanderButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background-color: white;
  /* Text glyph, not an icon font/SVG — always renders. */
  color: ${({ theme }) => theme.colors.primary};
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
`;

const StatusIcon = ({ status }) => {
  if (status === 'approved') return <FiCheck />;
  if (status === 'pending') return <FiClock />;
  if (status === 'challenged') return <FiX />;
  return null;
};

// Format a Date as a local 'YYYY-MM-DD' string (no UTC shift / off-by-one)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DetailedReport = () => {
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState({
    startDate: formatLocalDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
    endDate: formatLocalDate(new Date()),
    employeeId: '',
    customerId: '',
    status: ''
  });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Expanded employee groups (REQ #18), keyed by employeeName. Default collapsed.
  const [expandedEmployees, setExpandedEmployees] = useState({});

  // Fetch customers for the filter dropdown: those active in the selected
  // period (has hours or an assignment overlapping it) — so a report over a
  // past period still offers customers/employees who have since been
  // deactivated. Sorted alphabetically. Clears a selection that drops out.
  React.useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const customersResponse = await adminService.getCustomers({
          ...(filters.startDate && filters.endDate
            ? { activeFrom: filters.startDate, activeTo: filters.endDate }
            : { isActive: true }),
          limit: 1000,
          page: 1
        });
        const customerList = (customersResponse.customers || [])
          .slice()
          .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
        setCustomers(customerList);
        setFilters(prev => (
          prev.customerId && !customerList.some(c => String(c.id) === String(prev.customerId))
            ? { ...prev, customerId: '' }
            : prev
        ));
      } catch (error) {
        console.error('Error fetching filter data:', error);
      }
    };

    fetchCustomers();
  }, [filters.startDate, filters.endDate]);

  // Fetch employees scoped to the selected customer (REQ #17) and to the
  // selected period; sorted alphabetically. Clears the selected employee when
  // it drops out of the new list.
  React.useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const employeesResponse = await adminService.getEmployees({
          ...(filters.customerId && { customerId: filters.customerId }),
          ...(filters.startDate && filters.endDate
            ? { activeFrom: filters.startDate, activeTo: filters.endDate }
            : { isActive: true }),
          limit: 1000,
          page: 1
        });
        const employeeList = (employeesResponse.employees || [])
          .slice()
          .sort((a, b) =>
            `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(
              `${b.firstName || ''} ${b.lastName || ''}`,
              undefined,
              { sensitivity: 'base' }
            )
          );
        setEmployees(employeeList);
        setFilters(prev => (
          prev.employeeId && !employeeList.some(emp => String(emp.id) === String(prev.employeeId))
            ? { ...prev, employeeId: '' }
            : prev
        ));
      } catch (error) {
        console.error('Error fetching filter data:', error);
      }
    };

    fetchEmployees();
  }, [filters.customerId, filters.startDate, filters.endDate]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Only send non-empty filter values
  const buildReportParams = () => ({
    startDate: filters.startDate,
    endDate: filters.endDate,
    ...(filters.employeeId && { employeeId: filters.employeeId }),
    ...(filters.customerId && { customerId: filters.customerId }),
    ...(filters.status && { status: filters.status })
  });

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      const response = await reportService.generateAdminReport(buildReportParams());
      setReportData(response);
      setExpandedEmployees({});
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleEmployee = (employeeName) => {
    setExpandedEmployees(prev => ({ ...prev, [employeeName]: !prev[employeeName] }));
  };

  // Group the flat entry list per employee (REQ #18): entry count + total
  // hours per employee, alphabetical (sv/da case-insensitive). Keyed on the
  // employee id (name only as legacy fallback) so two employees sharing a
  // display name never merge into one group.
  const groupedEmployees = React.useMemo(() => {
    const groups = new Map();
    (reportData?.entries || []).forEach(entry => {
      const key = entry.employeeId != null ? `id:${entry.employeeId}` : (entry.employeeName || '');
      if (!groups.has(key)) {
        groups.set(key, { key, name: entry.employeeName || '', entries: [], totalHours: 0 });
      }
      const group = groups.get(key);
      group.entries.push(entry);
      group.totalHours += Number(entry.hours) || 0;
    });
    return [...groups.values()].sort((a, b) =>
      a.name.localeCompare(b.name, ['sv', 'da'], { sensitivity: 'base' })
    );
  }, [reportData]);

  const handleExportCsv = async () => {
    try {
      const blob = await reportService.exportAdminReportToCsv(buildReportParams());
      reportService.downloadFile(blob, 'time-report.csv');
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  return (
    <ReportContainer>
      <ReportHeader>
        <ReportTitle>Detailed Time Report</ReportTitle>
      </ReportHeader>
      
      <Card padding="md">
        <FiltersContainer>
          <FilterGroup>
            <FilterLabel htmlFor="startDate">Start Date</FilterLabel>
            <FilterInput
              type="date"
              id="startDate"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel htmlFor="endDate">End Date</FilterLabel>
            <FilterInput
              type="date"
              id="endDate"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel htmlFor="employeeId">Employee</FilterLabel>
            <FilterSelect
              id="employeeId"
              name="employeeId"
              value={filters.employeeId}
              onChange={handleFilterChange}
            >
              <option value="">All Employees</option>
              {employees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.firstName} {employee.lastName}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel htmlFor="customerId">Customer</FilterLabel>
            <FilterSelect
              id="customerId"
              name="customerId"
              value={filters.customerId}
              onChange={handleFilterChange}
            >
              <option value="">All Customers</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </FilterSelect>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel htmlFor="status">Status</FilterLabel>
            <FilterSelect
              id="status"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="challenged">Challenged</option>
            </FilterSelect>
          </FilterGroup>
          
          <FilterActions>
            <Button 
              onClick={handleGenerateReport}
              disabled={loading}
            >
              <FiSearch style={{ marginRight: '8px' }} />
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
            
            <Button 
              variant="outline"
              onClick={handleExportCsv}
              disabled={!reportData}
            >
              <FiDownload style={{ marginRight: '8px' }} />
              Export CSV
            </Button>
          </FilterActions>
        </FiltersContainer>
        
        {loading ? (
          <EmptyState>Generating report...</EmptyState>
        ) : reportData ? (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <tr>
                    <TableHeadCell style={{ width: 40 }} />
                    <TableHeadCell>Date</TableHeadCell>
                    <TableHeadCell>Customer</TableHeadCell>
                    <TableHeadCell>Project</TableHeadCell>
                    <TableHeadCell>Hours</TableHeadCell>
                    <TableHeadCell>Status</TableHeadCell>
                    <TableHeadCell>Description</TableHeadCell>
                  </tr>
                </TableHead>
                <tbody>
                  {groupedEmployees.length > 0 ? (
                    groupedEmployees.map(group => (
                      <React.Fragment key={group.key}>
                        <GroupRow onClick={() => toggleEmployee(group.key)}>
                          <GroupCell>
                            <ExpanderButton
                              type="button"
                              aria-expanded={!!expandedEmployees[group.key]}
                              aria-label={expandedEmployees[group.key]
                                ? `Collapse entries for ${group.name}`
                                : `Expand entries for ${group.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleEmployee(group.key);
                              }}
                            >
                              {expandedEmployees[group.key] ? '−' : '+'}
                            </ExpanderButton>
                          </GroupCell>
                          <GroupCell colSpan={2}>{group.name}</GroupCell>
                          <GroupMetaCell>
                            {group.entries.length} {group.entries.length === 1 ? 'entry' : 'entries'}
                          </GroupMetaCell>
                          <GroupCell>
                            {(Math.round(group.totalHours * 100) / 100).toLocaleString()}
                          </GroupCell>
                          <GroupMetaCell colSpan={2} />
                        </GroupRow>
                        {expandedEmployees[group.key] && group.entries.map(entry => (
                          <TableRow key={entry.id}>
                            <TableCell />
                            <TableCell>{entry.date}</TableCell>
                            <TableCell>{entry.customerName}</TableCell>
                            <TableCell>{entry.project}</TableCell>
                            <TableCell>{entry.hours}</TableCell>
                            <TableCell>
                              <StatusBadge status={entry.status}>
                                <StatusIcon status={entry.status} />
                                {entry.status
                                  ? entry.status.charAt(0).toUpperCase() + entry.status.slice(1)
                                  : 'Pending'}
                              </StatusBadge>
                            </TableCell>
                            <TableCell>{entry.description}</TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} style={{ textAlign: 'center' }}>
                        No entries found for the selected criteria.
                      </TableCell>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            </TableContainer>
            
            <SummaryFooter>
              <FooterText>
                Total Entries: <span>{(reportData.total?.entries ?? 0).toLocaleString()}</span>
              </FooterText>
              <FooterText>
                Total Hours: <span>{(reportData.total?.hours ?? 0).toLocaleString()}</span>
              </FooterText>
            </SummaryFooter>
          </>
        ) : (
          <EmptyState>
            <FiFilter style={{ marginBottom: '8px', opacity: 0.5 }} />
            <p>Select filters and generate a report to see data</p>
          </EmptyState>
        )}
      </Card>
    </ReportContainer>
  );
};

export default DetailedReport;